import {
  CCard,
  CCardBody,
  CCardTitle,
  CSmartTable,
  CButton,
  CRow,
  CCol,
  CAlert,
  CToaster,
  CToast,
  CToastClose,
  CToastBody,
  CFormInput,
  CFormLabel,
  CInputGroup,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
  CMultiSelect,
  CButtonGroup,
} from "@coreui/react-pro";
import CIcon from "@coreui/icons-react";
import {
  cilPlus,
  cilTrash,
  cilCheckCircle,
  cilSearch,
} from "@coreui/icons-pro";
import { assigneeAdapter } from "../../../../types";
import { usePssAssigneeDetails } from "../../controllers";
import { DeleteAssigneeModal } from "../modals";
import { AssigneeFilter } from "../../components";
import { ChangeEvent } from "react";
import "../sass/pss.assignee-details.scss";

const PssAssigneeDetail = (): JSX.Element => {
  // Controllers & Hooks
  const [assigneeNameStates, assigneeNameController] = usePssAssigneeDetails();

  const columns = assigneeNameStates.columns;
  const showSuccessToast = assigneeNameStates.showSuccessToast;
  const successToastMessage = assigneeNameStates.successToastMessage;
  const itemsPerPage = assigneeNameStates.itemsPerPage;
  const items = assigneeNameStates.items.list;
  const media = assigneeNameStates.media;
  const activePage = assigneeNameStates.activePage;
  const pages = assigneeNameStates.pages;
  const deleteVisible = assigneeNameStates.deleteVisible;

  const handleSearch = (name: string) => {
    const event = {
      target: {
        value: name,
      },
    } as ChangeEvent<HTMLInputElement>;

    assigneeNameController.onAssigneeSearch(event);
  };

  // Actions
  const setActivePage = assigneeNameController.setActivePage;
  const setItemsPerPage = assigneeNameController.setItemsPerPage;
  const setDeleteVisible = assigneeNameController.setDeleteVisible;
  const setRowData = assigneeNameController.setRowData;

  const colIdFormatter = (item: assigneeAdapter | undefined, index: number) => {
    const calculatedIndex =
      (assigneeNameStates.activePage - 1) * assigneeNameStates.itemsPerPage +
      index +
      1;
    return <td className="text-center">{calculatedIndex ?? "-"}</td>;
  };

  const colNameFormatter = (item: assigneeAdapter | undefined) => {
    return <td className="text-left align-middle">{item?.name ?? "-"}</td>;
  };
  const colDepartmentFormatter = (item: assigneeAdapter | undefined) => {
    return <td hidden={media.max.lg}>{item?.department ?? "-"}</td>;
  };
  const colPositionFormatter = (item: assigneeAdapter | undefined) => {
    return <td hidden={media.max.lg}>{item?.position ?? "-"}</td>;
  };
  const colContactNoFormatter = (item: assigneeAdapter | undefined) => {
    return <td hidden={media.max.lg}>{item?.contactNo ?? "-"}</td>;
  };
  const colEmailFormatter = (item: assigneeAdapter | undefined) => {
    return <td className="text-left align-middle">{item?.email ?? "-"}</td>;
  };

  const colActionFormatter = (item: assigneeAdapter) => {
    return (
      <td>
        <CButton
          className="ms-3"
          size="sm"
          variant="outline"
          color="primary"
          onClick={(_e) => {
            setRowData(item);
            setDeleteVisible(!deleteVisible);
          }}
        >
          <CIcon
            icon={cilTrash}
            size="sm"
            onClick={() => assigneeNameController.onDeleteAssignee(item)}
          />
        </CButton>
      </td>
    );
  };

  return (
    <CCard className=" assignee p-2 mb-3">
      <CCardTitle
        style={{
          borderBottom: "1px solid rgba(0, 0, 0, 0.125)",
          padding: "10px 15px",
          marginBottom: "10px",
        }}
      >
        <CRow className="align-items-center">
          <CCol xs={6}>
            <h5 className="fw-semibold">Assignee / List of Assignee</h5>
          </CCol>
          <CCol className="ms-md-auto text-end">
            <CButton onClick={assigneeNameController.onAddNewAssignee}>
              <CIcon icon={cilPlus} size="lg" className="me-2 align-middle" />
              <span>Add New Assignee</span>
            </CButton>
          </CCol>
        </CRow>
      </CCardTitle>
      <AssigneeFilter
        state={{
          searchAssigneeName: assigneeNameStates.searchAssigneeNameValue,
        }}
        controller={{
          onSearchClicked: assigneeNameController.onSearchClicked,
          onSyncClicked: assigneeNameController.onSyncClicked,
          onAssigneeSearch: handleSearch,
        }}
      />
      <CCardBody>
        <CSmartTable
          loading={assigneeNameStates.loading}
          activePage={1}
          clickableRows
          columns={columns}
          items={items}
          itemsPerPage={itemsPerPage}
          itemsPerPageSelect
          pagination={{
            external: true,
          }}
          paginationProps={{
            activePage: activePage,
            pages: pages,
          }}
          onActivePageChange={(activePage) => setActivePage(activePage)}
          onItemsPerPageChange={(itemsPerPage) => {
            setActivePage(1);
            setItemsPerPage(itemsPerPage);
          }}
          noItemsLabel={
            <div className="text-center text-danger text-bg-light">
              Record not found
            </div>
          }
          tableProps={{
            hover: true,
            striped: true,
            small: true,
            responsive: true,
          }}
          tableBodyProps={{
            className: "align-middle text-center",
          }}
          sorterValue={{ column: "id", state: "asc" }}
          scopedColumns={{
            id: colIdFormatter,

            name: colNameFormatter,
            department: colDepartmentFormatter,
            position: colPositionFormatter,
            contactNo: colContactNoFormatter,
            email: colEmailFormatter,
            actions: colActionFormatter,
          }}
        />
      </CCardBody>
      <CModal
        backdrop="static"
        visible={assigneeNameStates.newAssigneeModalVisible}
        onClose={assigneeNameController.onAddNewAssigneeClose}
        aria-labelledby="AddDutyLocationModal"
      >
        <CModalHeader className="card-title">
          <CModalTitle>
            <strong>Assignee</strong>
          </CModalTitle>
        </CModalHeader>
        <CModalBody>
          <CRow className="mb-4">
            <CCol xs="12" sm="3" lg="3">
              <CFormLabel>Name </CFormLabel>
            </CCol>
            <CCol style={{ width: "250px" }}>
              <CInputGroup>
                <CCol>
                  <CFormInput
                    id="inputForm1"
                    className="filter-input"
                    placeholder="Search..."
                    valid={assigneeNameStates.name.valid}
                    invalid={assigneeNameStates.name.invalid}
                    feedbackValid={assigneeNameStates.name.feedbackValid}
                    feedbackInvalid={assigneeNameStates.name.feedbackInvalid}
                    onChange={assigneeNameController.onAssigneeNameSearch}
                  />
                  {assigneeNameStates.showDropdown && (
                    <CMultiSelect
                      valid={assigneeNameStates.name.valid}
                      invalid={assigneeNameStates.name.invalid}
                      feedbackValid={assigneeNameStates.name.feedbackValid}
                      feedbackInvalid={assigneeNameStates.name.feedbackInvalid}
                      loading={
                        assigneeNameStates.findUserAssigneeStatus.isLoading
                      }
                      options={
                        assigneeNameStates.currentSelection
                          ? [assigneeNameStates.currentSelection.name]
                          : assigneeNameStates.newNameOptions
                      }
                      onChange={(e) => {
                        assigneeNameController.onNewNameSelectionChange(e);
                        assigneeNameController.nameController.setValues(
                          e.map((option) => option.value.toString()),
                        );
                      }}
                      virtualScroller
                      multiple={false}
                    />
                  )}
                </CCol>
                <CCol xs="auto" className="text-end">
                  <CButtonGroup>
                    <CButton
                      color="grey"
                      onClick={assigneeNameController.handleSearchButtonClick}
                    >
                      <CIcon icon={cilSearch} title="Search" />
                    </CButton>
                  </CButtonGroup>
                </CCol>
              </CInputGroup>
            </CCol>
          </CRow>
          <CRow className="mb-4 hidden">
            <CCol className="col-sm-4">
              <CFormLabel htmlFor="userEmailInput" className="col-form-label">
                User Id
              </CFormLabel>
            </CCol>
            <CCol>
              <CMultiSelect
                id="userCaller"
                valid={assigneeNameStates.userAssigneeId.valid}
                invalid={assigneeNameStates.userAssigneeId.invalid}
                feedbackValid={assigneeNameStates.userAssigneeId.feedbackValid}
                feedbackInvalid={
                  assigneeNameStates.userAssigneeId.feedbackInvalid
                }
                options={
                  assigneeNameStates.currentSelection
                    ? [assigneeNameStates.currentSelection.id]
                    : assigneeNameStates.idOptions
                }
                onChange={(e) => {
                  assigneeNameController.onNewIdSelectionChange(e);
                  assigneeNameController.userIdController.setValues(
                    e.map((option) => option.value.toString()),
                  );
                }}
                onFocus={() => {
                  assigneeNameController.userIdController.onFocus();
                }}
                multiple={false}
                required
                disabled
              />
            </CCol>
          </CRow>
          <CRow className="mb-4 ">
            <CCol xs="12" sm="3" lg="3">
              <CFormLabel>Department </CFormLabel>
            </CCol>
            <CCol>
              <CMultiSelect
                disabled
                loading={assigneeNameStates.findUserAssigneeStatus.isLoading}
                multiple={false}
                options={
                  assigneeNameStates.currentSelection
                    ? [assigneeNameStates.currentSelection.department]
                    : assigneeNameStates.departmentOptions
                }
                onChange={assigneeNameController.onNewDepartmentSelectionChange}
                virtualScroller
                placeholder=""
              />
            </CCol>
          </CRow>
          <CRow className="mb-4 ">
            <CCol xs="12" sm="3" lg="3">
              <CFormLabel>Position </CFormLabel>
            </CCol>
            <CCol>
              <CMultiSelect
                disabled
                loading={assigneeNameStates.findUserAssigneeStatus.isLoading}
                multiple={false}
                options={
                  assigneeNameStates.currentSelection
                    ? [assigneeNameStates.currentSelection.position]
                    : assigneeNameStates.positionOptions
                }
                onChange={assigneeNameController.onNewPositionSelectionChange}
                virtualScroller
                placeholder=""
              />
            </CCol>
          </CRow>
          <CRow className="mb-4 ">
            <CCol xs="12" sm="3" lg="3">
              <CFormLabel>Contact No </CFormLabel>
            </CCol>
            <CCol>
              <CMultiSelect
                disabled
                loading={assigneeNameStates.findUserAssigneeStatus.isLoading}
                multiple={false}
                options={
                  assigneeNameStates.currentSelection
                    ? [assigneeNameStates.currentSelection.contactNo]
                    : assigneeNameStates.contactNoOptions
                }
                onChange={assigneeNameController.onNewContactNoSelectionChange}
                virtualScroller
                placeholder=""
              />
            </CCol>
          </CRow>
          <CRow className="mb-4 ">
            <CCol xs="12" sm="3" lg="3">
              <CFormLabel>Email </CFormLabel>
            </CCol>
            <CCol>
              <CMultiSelect
                disabled
                multiple={false}
                loading={assigneeNameStates.findUserAssigneeStatus.isLoading}
                options={
                  assigneeNameStates.currentSelection
                    ? [assigneeNameStates.currentSelection.email]
                    : assigneeNameStates.emailOptions
                }
                onChange={assigneeNameController.onNewEmailSelectionChange}
                virtualScroller
                placeholder=""
              />
            </CCol>
          </CRow>
        </CModalBody>
        <CModalFooter>
          <CButton
            style={{
              width: "20%",
              fontWeight: "0.9rem",
              backgroundColor: "#2D3569",
            }}
            onClick={() => {
              assigneeNameController.onSubmit();
            }}
          >
            Add
          </CButton>
        </CModalFooter>
      </CModal>

      <DeleteAssigneeModal
        state={{
          visible: assigneeNameStates.deleteAssigneeModalVisible,
          name: assigneeNameStates.name,
          id: assigneeNameStates.userId,
        }}
        controller={{
          onClose: assigneeNameController.onDeleteAssigneeClose,
          onDelete: (item: assigneeAdapter | undefined) =>
            assigneeNameController.onDelete(item),
        }}
      />
      <CAlert className="mt-3 text-center" color="danger" visible={false}>
        {assigneeNameStates.error?.message}
      </CAlert>
      {/* ---- Success Toast ---------*/}
      <CToaster placement="top-end">
        <CToast
          autohide={true}
          visible={showSuccessToast}
          color="success"
          className="text-white align-items-center"
        >
          <div className="d-flex justify-content-center align-items-center">
            <CIcon
              icon={cilCheckCircle}
              size="xl"
              style={{ marginLeft: "12px" }}
            />
            <CToastBody>{successToastMessage}</CToastBody>
            <CToastClose className="me-2 m-auto" white />
          </div>
        </CToast>
      </CToaster>
    </CCard>
  );
};

export default PssAssigneeDetail;
